"use strict";
// // // // // // // // // // // // // // // // // // // // // // // //
// Protocol Creation Constants
// // // // // // // // // // // // // // // // // // // // // // // //
Object.defineProperty(exports, "__esModule", { value: true });
const types_1 = require("./types");
exports.UUID_V4_VALIDATION_REGEX = /[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89aAbB][a-f0-9]{3}-[a-f0-9]{12}/;
const ONE_SECOND_MS = 1000;
const DEFAULT_MELT_DURATION = 20;
const MAX_TEMPERATURE = 105;
const MIN_TEMPERATURE = 47;
const MIN_ANNEAL_TIME = 9;
const MIN_TEMPERATURE_ISO = 35;
const MIN_TEMPERATURE_RT = 35;
const MIN_DURATION_ISO = 10;
const MAX_DURATION_ISO = 60;
const MIN_TEMPERATURE_MELT = 35;
const MAX_TEMPERATURE_MELT = 95;
const MIN_DURATION_MELT = 20;
const MAX_DURATION_MELT = 60;
const PROTOCOL_VERSION = "v5"; // this should be incremented up for each app migration that affects protocols.
const CUSTOM_PROTOCOL_VERSION = "v3"; // this should always be kept at v3 unless we have a reason to deviate.
const SUPPORTED_PROTOCOL_VERSIONS = ["v2", "v3", "v4", "v5"];
const ALLOWED_TEMP_CHARACTERS_WARNING = "Temperature must be a number";
const ALLOWED_DURATION_CHARACTERS_WARNING = "Duration must be a number";
const MAX_DURATION = 3600;
const EXTENSION_OVERSHOOT_OFFSET = 6;
const EXTENSION_OVERSHOOT_DURATION = 5000;
const ATTO_TO_TEX_COEXCITATION_VALUE = 0.014;
const TEX_TO_ATTO_COEXCITATION_VALUE = 0.043;
const INITIAL_PROTOCOL_VALUES = {
    name: "",
    greenTargetName: "",
    amberTargetName: "",
    redTargetName: "",
    firstDenatureTime: 60,
    firstDenatureTemp: 95,
    denatureTemp: 95,
    denatureTime: 1,
    annealTemp: 60,
    annealTime: 20,
    rtTemp: 48,
    rtTime: 120,
    showRT: false,
    extensionTemp: 72,
    extensionTime: 20,
    showExtension: false,
    cycles: 45,
    greenActive: 1,
    amberActive: 1,
    redActive: 1,
    showIsothermal: false,
    isothermalTemp: 65,
    isothermalSampleFrequency: 4,
    isothermalTotalTime: 60,
    showHeatingMelt: false,
    showCoolingMelt: false,
    meltTempStart: 0,
    meltTempFinal: 0,
    meltIncrement: 1.0,
};
const EMPTY_PROTOCOL_STRUCT = {
    id: "",
    sku: "",
    teamId: "",
    timestamp: 0,
    type: "1to1",
    firstDenatureTime: 1,
    firstDenatureTemp: 95,
    denature: 70,
    protocolType: types_1.RunType.Qualitative,
    denatureTime: 1 * ONE_SECOND_MS,
    anneal: 60,
    annealTime: 1 * ONE_SECOND_MS,
    undershoot: 48,
    undershootTime: 2 * ONE_SECOND_MS,
    overshoot: 70,
    overshootTime: 1 * ONE_SECOND_MS,
    rtTemp: 0,
    rtTime: 0,
    extensionTemp: 0,
    extensionTime: 0,
    red: 255,
    green: 255,
    blue: 255,
    amber: 255,
    cycles: 45,
    wellMask: 7,
    colorMask: 7,
    generic: true,
    synced: false,
    isHidden: false,
    name: "",
    showSampleType: false,
    targets: [
        {
            name: "",
            abbreviation: "",
            hidden: false,
            notes: "",
            groupAbbreviation: "",
            group: "",
            fluorophore: "TexRedX",
            excitationColor: "amber",
            emissionColor: "amber",
            sampleId: "",
            sourceId: "",
            meltTemperatures: [],
            wellNumber: 0,
            strip: "A",
            well: "left",
            coexcitation: {
                level: ATTO_TO_TEX_COEXCITATION_VALUE,
                sourceExcitationColor: "red",
            },
        },
        {
            name: "",
            abbreviation: "",
            hidden: false,
            notes: "",
            groupAbbreviation: "",
            group: "",
            fluorophore: "ATTO-647N",
            excitationColor: "red",
            emissionColor: "red",
            well: "left",
            meltTemperatures: [],
            wellNumber: 0,
            strip: "A",
            sampleId: "",
            sourceId: "",
            coexcitation: {
                level: TEX_TO_ATTO_COEXCITATION_VALUE,
                sourceExcitationColor: "amber",
            },
        },
        {
            name: "",
            abbreviation: "",
            hidden: false,
            notes: "",
            groupAbbreviation: "",
            group: "",
            fluorophore: "FAM",
            excitationColor: "blue",
            emissionColor: "green",
            well: "left",
            meltTemperatures: [],
            wellNumber: 0,
            strip: "A",
            sampleId: "",
            sourceId: "",
        },
        {
            name: "",
            abbreviation: "",
            hidden: false,
            notes: "",
            groupAbbreviation: "",
            group: "",
            fluorophore: "TexRedX",
            excitationColor: "amber",
            emissionColor: "amber",
            well: "middle",
            meltTemperatures: [],
            wellNumber: 1,
            strip: "A",
            sampleId: "",
            sourceId: "",
            coexcitation: {
                level: ATTO_TO_TEX_COEXCITATION_VALUE,
                sourceExcitationColor: "red",
            },
        },
        {
            name: "",
            abbreviation: "",
            hidden: false,
            notes: "",
            groupAbbreviation: "",
            group: "",
            fluorophore: "ATTO-647N",
            excitationColor: "red",
            emissionColor: "red",
            well: "middle",
            wellNumber: 1,
            strip: "A",
            meltTemperatures: [],
            sampleId: "",
            sourceId: "",
            coexcitation: {
                level: TEX_TO_ATTO_COEXCITATION_VALUE,
                sourceExcitationColor: "amber",
            },
        },
        {
            name: "",
            abbreviation: "",
            hidden: false,
            notes: "",
            groupAbbreviation: "",
            group: "",
            fluorophore: "FAM",
            excitationColor: "blue",
            emissionColor: "green",
            meltTemperatures: [],
            well: "middle",
            wellNumber: 1,
            strip: "A",
            sampleId: "",
            sourceId: "",
        },
        {
            name: "",
            abbreviation: "",
            hidden: false,
            notes: "",
            groupAbbreviation: "",
            group: "",
            fluorophore: "TexRedX",
            excitationColor: "amber",
            emissionColor: "amber",
            meltTemperatures: [],
            well: "right",
            wellNumber: 2,
            strip: "A",
            sampleId: "",
            sourceId: "",
            coexcitation: {
                level: ATTO_TO_TEX_COEXCITATION_VALUE,
                sourceExcitationColor: "red",
            },
        },
        {
            name: "",
            abbreviation: "",
            hidden: false,
            notes: "",
            groupAbbreviation: "",
            group: "",
            fluorophore: "ATTO-647N",
            excitationColor: "red",
            emissionColor: "red",
            meltTemperatures: [],
            well: "right",
            wellNumber: 2,
            strip: "A",
            sampleId: "",
            sourceId: "",
            coexcitation: {
                level: TEX_TO_ATTO_COEXCITATION_VALUE,
                sourceExcitationColor: "amber",
            },
        },
        {
            name: "",
            abbreviation: "",
            hidden: false,
            notes: "",
            groupAbbreviation: "",
            group: "",
            fluorophore: "FAM",
            excitationColor: "blue",
            emissionColor: "green",
            meltTemperatures: [],
            well: "right",
            wellNumber: 2,
            strip: "A",
            sampleId: "",
            sourceId: "",
        },
    ],
};
exports.protocolCreationConstants = {
    ONE_SECOND_MS,
    DEFAULT_MELT_DURATION,
    MAX_TEMPERATURE,
    MIN_TEMPERATURE,
    MIN_ANNEAL_TIME,
    MIN_TEMPERATURE_ISO,
    MIN_TEMPERATURE_RT,
    MIN_DURATION_ISO,
    MAX_DURATION_ISO,
    MIN_TEMPERATURE_MELT,
    MAX_TEMPERATURE_MELT,
    MIN_DURATION_MELT,
    MAX_DURATION_MELT,
    PROTOCOL_VERSION,
    CUSTOM_PROTOCOL_VERSION,
    SUPPORTED_PROTOCOL_VERSIONS,
    ALLOWED_TEMP_CHARACTERS_WARNING,
    ALLOWED_DURATION_CHARACTERS_WARNING,
    MAX_DURATION,
    EXTENSION_OVERSHOOT_OFFSET,
    EXTENSION_OVERSHOOT_DURATION,
    INITIAL_PROTOCOL_VALUES,
    EMPTY_PROTOCOL_STRUCT,
};
